import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData, isNumeric } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global, globalMenu } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { showPopupModal } from "@widgets/CommonModal";
import ExcelUpload from "@widgets/commonpopup/ExcelUpload";
import SN_SAL_BAS_REG_002_POP_01 from "./SN_SAL_BAS_REG_002_POP_01";
import SnButton from "@shared/components/SnButton";
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import { dateToString } from "@shared/utils/DateUtils";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import SnResizeBar from "@shared/components/SnResizeBar";
import * as XLSX from "xlsx";

interface Props {
  Menuprops: menuInfo;
}

// 엑셀 업로드 시 기존 데이터 여부를 판정할 PK 조합 (mas_umcust)
const UPLOAD_PK_FIELDS = [
  "cd_firm",
  "cd_module",
  "cd_cust",
  "cd_item",
  "tp_um",
  "cd_exch",
  "dt_start",
];

// 엑셀 업로드 오류체크(필수값) 대상 컬럼
// 거래처/품목 중 하나는 좌측 목록에서 선택한 값으로 채우므로 탭별로 다르다.
const UPLOAD_REQUIRED_COMMON = [
  "cd_exch",
  "tp_um",
  "um",
  "um_supply",
  "dt_start",
];
// 업로드 양식은 탭과 무관하게 동일하므로 필수값도 하나로 쓴다.
const UPLOAD_REQUIRED_FIELDS = [
  "cd_item",
  "cd_cust",
  ...UPLOAD_REQUIRED_COMMON,
];

// 엑셀 업로드 양식 컬럼 (탭과 무관하게 이 순서로 고정)
const UPLOAD_FORM_COLUMNS: {
  cd_column: string;
  nm_column: string;
  qt_width?: number; // 지정하면 업로드 양식에서 이 너비로 고정
}[] = [
  { cd_column: "cd_item", nm_column: "품목코드" },
  { cd_column: "nm_item", nm_column: "품목명" },
  { cd_column: "cd_cust", nm_column: "거래처", qt_width: 160 },
  { cd_column: "nm_cust", nm_column: "거래처명" },
  { cd_column: "dc_item_spec", nm_column: "규격" },
  { cd_column: "dc_item_unit", nm_column: "단위" },
  { cd_column: "cd_exch", nm_column: "환종" },
  { cd_column: "tp_um", nm_column: "단가유형" },
  { cd_column: "um", nm_column: "단가" },
  { cd_column: "um_supply", nm_column: "부가세포함단가" },
  { cd_column: "dt_start", nm_column: "시작일" },
  { cd_column: "dt_end", nm_column: "종료일" },
  { cd_column: "dc_rmk", nm_column: "비고" },
  { cd_column: "um_mgmt1", nm_column: "관리단가1" },
  { cd_column: "um_mgmt2", nm_column: "관리단가2" },
  { cd_column: "um_mgmt3", nm_column: "관리단가3" },
  { cd_column: "um_mgmt4", nm_column: "관리단가4" },
  { cd_column: "um_mgmt5", nm_column: "관리단가5" },
];

// 종료일 기본값 (행추가 / 엑셀 업로드에서 종료일을 비워둔 경우)
const DEFAULT_DT_END = "21001231";

const SN_SAL_BAS_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
    grid4: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });

  const [selectedTab, setSelectedTab] = useState<string>("1");
  const [initFlag, setInitFlag] = useState<boolean>(false);

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
        });
        setInitFlag(true);
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (initFlag) onSearch();
  }, [selectedTab]);

  useImperativeHandle(ref, () => ({
    onSearch,
    onSave,
    onExcelDownLoad,
    onExcelUpload,
  }));

  const onSearch = async () => {
    const modifiedRows1 = gridRefs.current["grid2"].current.getChange();
    const modifiedRows2 = gridRefs.current["grid4"].current.getChange();
    if (
      modifiedRows1.modifiedRowcount > 0 ||
      modifiedRows2.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id:
          selectedTab === "1"
            ? "sn_sal_bas_reg_002_01"
            : "sn_sal_bas_reg_002_02",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();
      gridRefs.current["grid4"].current.resetUpdate();

      const data: any = await fnGetDataSet(params);
      if (selectedTab === "1") {
        setDataSources({
          grid1: data[0] || [],
          grid2: data[1] || [],
          grid3: [],
          grid4: [],
        });
      } else if (selectedTab === "2") {
        setDataSources({
          grid1: [],
          grid2: [],
          grid3: data[0] || [],
          grid4: data[1] || [],
        });
      }
    } catch (error: any) {}
  };

  const onAddRow = () => {
    try {
      if (selectedTab === "1") {
        return gridRefs.current["grid2"].current?.addRow();
      } else if (selectedTab === "2") {
        return gridRefs.current["grid4"].current?.addRow();
      }
    } catch (error: any) {}
  };

  const onBeforeAddRow1 = (): any => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (!focusedNodeH) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      return {
        cd_firm: userInfo.cd_firm,
        cd_module: "SAL",
        cd_cust: focusedNodeH.data?.cd_cust,
        tp_um: searchInfo.tp_um,
        dt_start: today,
        dt_end: DEFAULT_DT_END,
        cd_exch: searchInfo.cd_exch,
        um: 0,
        um_supply: 0,
        cd_user_reg: userInfo.cd_user,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onBeforeAddRow2 = (): any => {
    try {
      gridRefs.current["grid3"].current?.stopEditing();
      const focusedNodeH = gridRefs.current["grid3"].current.focusedRowNode();
      if (!focusedNodeH) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      return {
        cd_firm: userInfo.cd_firm,
        cd_module: "SAL",
        cd_item: focusedNodeH.data?.cd_item,
        tp_um: searchInfo.tp_um,
        cd_exch: searchInfo.cd_exch,
        dt_start: today,
        dt_end: DEFAULT_DT_END,
        um: 0,
        um_supply: 0,
        qt_dummy01: 0,
        um_before: 0,
        um_mgmt1: 0,
        um_mgmt2: 0,
        um_mgmt3: 0,
        um_mgmt4: 0,
        um_mgmt5: 0,
        cd_user_reg: userInfo.cd_user,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDeleteRow = async () => {
    if (selectedTab === "1") {
      if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) return;
      gridRefs.current["grid2"].current?.deleteSelectedRows();
    } else if (selectedTab === "2") {
      if (gridRefs.current["grid4"].current?.getDisplayedRowCount() < 1) return;
      gridRefs.current["grid4"].current?.deleteSelectedRows();
    }
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid2"].current?.stopEditing();
      gridRefs.current["grid4"].current?.stopEditing();

      let umCustChanges;
      if (selectedTab === "1") {
        const requiredValuesCheck1 =
          gridRefs.current["grid2"].current.requiredValuesCheck();
        if (!requiredValuesCheck1) return false;

        umCustChanges = gridRefs.current["grid2"].current.getChange();
      } else {
        const requiredValuesCheck2 =
          gridRefs.current["grid4"].current.requiredValuesCheck();
        if (!requiredValuesCheck2) return false;

        umCustChanges = gridRefs.current["grid4"].current.getChange();
      }

      if (umCustChanges.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const insertList = umCustChanges.insert ?? [];
      const updateList = umCustChanges.insert ?? [];
      const isValid = checkDatesValid([...insertList, ...updateList]);
      if (!isValid) return false;

      const saveData = {
        umcust: umCustChanges,
      };

      const ok = await fnSave("SN_SAL_BAS_REG_002_SAVE_01", saveData);
      if (!ok) return false;

      gridRefs.current["grid2"].current?.resetUpdate();
      gridRefs.current["grid4"].current?.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid2"].current.resetUpdate(true);
      gridRefs.current["grid4"].current.resetUpdate(true);
      return false;
    }
  };

  // 탭에 따라 단가 그리드가 다르다 (거래처별: grid2 / 품목별: grid4)
  const getUmGridRef = () =>
    selectedTab === "1"
      ? gridRefs.current["grid2"].current
      : gridRefs.current["grid4"].current;

  const getUmColumns = () =>
    selectedTab === "1" ? columns.grid2 : columns.grid4;

  /**
   * 엑셀 업로드 양식 컬럼.
   * 탭(거래처별/품목별)과 상관없이 UPLOAD_FORM_COLUMNS 순서 그대로 만든다.
   * 컬럼 정의는 두 단가 그리드(거래처별/품목별) 설정에서 찾아 쓰고,
   * 양쪽에 다 없으면 텍스트 컬럼으로 만들어 양식에서 빠지지 않게 한다.
   */
  const getUploadColumns = () => {
    const allColumns: any[] = [
      ...(columns.grid2 ?? []),
      ...(columns.grid4 ?? []),
    ];

    return UPLOAD_FORM_COLUMNS.map((form, index) => {
      const found = allColumns.find(
        (col: any) => col.cd_column === form.cd_column
      );

      if (found) {
        // 그리드 설정에 컬럼명이 비어 있으면 헤더에 필드코드가 그대로 찍히므로
        // 양식에 정의한 이름을 항상 쓴다.
        return {
          ...found,
          nm_column: form.nm_column,
          fg_visible: "Y",
          no_seq: index,
          ...(form.qt_width ? { qt_width: form.qt_width } : {}),
        };
      }

      return {
        cd_column: form.cd_column,
        nm_column: form.nm_column,
        tp_column: "SnTextBox",
        fg_visible: "Y",
        yn_required: "N",
        dc_editcon: "true",
        qt_width: form.qt_width ?? 120,
        no_seq: index,
      };
    });
  };

  /**
   * 엑셀 셀 값 변환.
   * 날짜(dt_*)는 화면 표기(2026/09/23)가 아니라 업로드 양식과 같은 yyyyMMdd 로,
   * 숫자는 숫자로, 나머지는 문자열로 넣는다.
   */
  const toExcelCellValue = (colId: string, value: any): string | number => {
    if (value == null) return "";

    const raw =
      typeof value === "object"
        ? value.label ?? value.text ?? value.value ?? ""
        : value;
    const text = raw?.toString() ?? "";

    if (colId.startsWith("dt_")) return text.replace(/[^0-9]/g, "");
    // 앞자리가 0 인 코드값은 숫자로 바꾸면 0 이 날아가므로 문자열로 둔다.
    if (text !== "" && !/^0\d/.test(text) && isNumeric(text)) {
      return Number(text);
    }

    return text;
  };

  /**
   * 엑셀 다운로드.
   * 엑셀 업로드 양식(UPLOAD_FORM_COLUMNS)과 컬럼 구성·순서를 똑같이 맞춰 내려준다.
   * (업로드는 붙여넣기 기준이라 컬럼 순서가 다르면 값이 밀린다)
   * forEachNode 는 필터와 무관하게 전체 행을 주므로, 좌측 목록에서 선택한
   * 거래처/품목 건만이 아니라 조회된 전체가 대상이 된다.
   */
  const onExcelDownLoad = async () => {
    const grid = getUmGridRef();
    if (!grid) return;

    try {
      const rows: any[] = [];
      grid.forEachNode((node: any) => {
        if (node.data) rows.push(node.data);
      });

      const sheetData = [
        UPLOAD_FORM_COLUMNS.map((col) => col.nm_column),
        ...rows.map((row) =>
          UPLOAD_FORM_COLUMNS.map((col) =>
            toExcelCellValue(col.cd_column, row[col.cd_column])
          )
        ),
      ];

      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        book,
        XLSX.utils.aoa_to_sheet(sheetData),
        "Sheet1"
      );
      XLSX.writeFile(
        book,
        `${globalMenu.getState().activeMenuInfo.nm_menu}.xlsx`
      );
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onExcelUpload = async () => {
    try {
      // 업로드 양식은 탭과 무관하게 동일하다.
      // 거래처·품목 모두 엑셀에서 받으므로 좌측 목록 선택값은 쓰지 않는다.
      const component = (
        <ExcelUpload
          Menuprops={Menuprops}
          PkFields={UPLOAD_PK_FIELDS}
          tableName="umcust" // SN_SAL_BAS_REG_002_SAVE_01 이 받는 데이터셋 키
          controllerName="SN_SAL_BAS_REG_002_SAVE_01"
          pkCheckCode="mas_umcust" // PK 중복 판정용 조회 코드
          defaultValue={{
            cd_firm: userInfo.cd_firm,
            cd_module: "SAL",
            cd_user_reg: userInfo.cd_user,
            // 종료일을 비워두고 올리면 행추가와 같은 기본값이 들어간다.
            dt_end: DEFAULT_DT_END,
          }}
          columnConfig={getUploadColumns()}
          requiredFields={UPLOAD_REQUIRED_FIELDS}
        />
      );

      const result = await showPopupModal({
        title: "엑셀 업로드",
        children: component,
        width: "80%",
        height: "90VH",
        okayBtnText: "저장",
      });

      if (result?.excelUpload) {
        await onSearch();
      }
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "addRow") {
      onAddRow();
    }
    if (id == "deleteRow") {
      onDeleteRow();
    }
    if (id == "btnNewItem") {
      if (selectedTab === "2") {
        await showDialog("alter", "거래처별 탭에서만 사용 가능합니다.");
        return;
      }

      const selectedFormData = searchRef.current.getFormdata();
      const focusedNodeH = gridRefs.current["grid1"]?.current?.focusedRowNode();
      if (!focusedNodeH) return;
      const selectedData = {
        tp_um: selectedFormData.tp_um,
        cd_cust: focusedNodeH?.data?.cd_cust || "",
        cd_cust_rcv: focusedNodeH?.data?.cd_cust || "",
        nm_cust: focusedNodeH?.data?.nm_cust || "",
        nm_cust_sort: focusedNodeH?.data?.nm_cust_sort || "",
      };

      const component = (
        <SN_SAL_BAS_REG_002_POP_01 selectedData={selectedData} />
      );
      const result = await showPopupModal({
        title: "신규품목적용",
        children: component,
        width: "65%",
        height: "60%",
        validation: true,
      });
      if (!result || result === "close") return;

      const lineResult: any[] = result[0];
      lineResult.map((result: any) => {
        const addedNode = onAddRow();
        if (addedNode) {
          addedNode.setDataValue("cd_item", result.cd_item);
          addedNode.setDataValue("nm_item", result.nm_item);
          addedNode.setDataValue("dc_item_spec", result.dc_item_spec);
          addedNode.setDataValue("dc_item_unit", result.dc_item_unit);
          addedNode.setDataValue("dc_material", result.dc_material);
          addedNode.setDataValue("cd_exch", result.cd_exch);
          addedNode.setDataValue("tp_um", result.tp_um);
          addedNode.setDataValue("um", result.um);
          addedNode.setDataValue("um_supply", result.um_supply);
          addedNode.setDataValue("dt_start", result.dt_start);
          addedNode.setDataValue("dt_end", result.dt_end);
          addedNode.setDataValue("dc_rmk", result.dc_rmk);
        }
      });
    }
  };

  const onGrid4BtnsClick = async (id: string) => {
    if (id == "addRow") {
      onAddRow();
    }
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      if (selectedTab === "1") {
        const cd_cust = selectedRow.cd_cust;
        gridRefs.current["grid2"].current.applyFilters({ cd_cust: cd_cust });
        gridRefs.current["grid2"].current.selectAndFocusRow(0);
      } else {
        const cd_item = selectedRow.cd_item;
        gridRefs.current["grid4"].current.applyFilters({ cd_item: cd_item });
        gridRefs.current["grid4"].current.selectAndFocusRow(0);
      }
    } else {
      return;
    }
  };

  const onCellValueChanged1 = (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "um") {
      const um_supply = +(rowData.um * 1.1);
      rowNode.setDataValue("um_supply", um_supply, "noEvent");
    }
    if (colId === "um_supply") {
      const um = +((rowData.um_supply * 10) / 11);
      rowNode.setDataValue("um", um, "noEvent");
    }
    if (colId === "dt_start" || colId === "dt_end") {
      checkDatesValid([rowData]);
    }
  };

  const onCellValueChanged2 = (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "um") {
      const um_supply = +(rowData.um * 1.1);
      rowNode.setDataValue("um_supply", um_supply, "noEvent");
    }
    if (colId === "um_supply") {
      const um = +((rowData.um_supply * 10) / 11);
      rowNode.setDataValue("um", um, "noEvent");
    }
    if (colId === "dt_start" || colId === "dt_end") {
      checkDatesValid([rowData]);
    }
  };

  const onTabChanged = async (index: string) => {
    const modifiedRows1 = gridRefs.current["grid2"].current.getChange();
    const modifiedRows2 = gridRefs.current["grid4"].current.getChange();
    if (
      modifiedRows1.modifiedRowcount > 0 ||
      modifiedRows2.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }
    }
    setSelectedTab(index);
  };

  const checkDatesValid = (rows: any[]): boolean => {
    for (const row of rows) {
      const dtStart = parseInt(row.dt_start?.replace(/-/g, "") || "0", 10);
      const dtEnd = parseInt((row.dt_end ?? "21001231").replace(/-/g, "") || 0);

      if (dtStart > dtEnd) {
        showDialog("alter", "시작일이 종료일보다 빠릅니다.");
        return false;
      }
    }
    return true;
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: { onFocusedRowChanged: onFocusedRowChanged },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRow1,
          onCellValueChanged: onCellValueChanged1,
          onBtnDeleteClick: onGrid2BtnsClick,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: { onFocusedRowChanged: onFocusedRowChanged },
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {
          onBeforeAddRow: onBeforeAddRow2,
          onCellValueChanged: onCellValueChanged2,
          onBtnDeleteClick: onGrid4BtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          
        ></SearchPanel>
      </div>

      <div className="flex-1">
        <SnTab
          titles={["거래처별", "폼목별"]}
          width={"100%"}
          onTabChanged={onTabChanged}
          header={[
            <SnTabHeader
              body={
                <div>
                  <SnButton
                    text={"신규품목적용"}
                    onClick={() => {
                      onGrid2BtnsClick("btnNewItem");
                    }}
                  ></SnButton>
                </div>
              }
              onBtnAddClick={onGrid2BtnsClick}
              onBtnDeleteClick={onGrid2BtnsClick}
            />,
            <SnTabHeader
              onBtnAddClick={onGrid4BtnsClick}
              onBtnDeleteClick={onGrid4BtnsClick}
            />,
          ]}
          body={[
            <div className="flex w-full h-full min-w-0 space-x-1.5">
              <SnResizeBar direction={"width"} id="panel1" orgSize="29.5%">
                <SnGrid
                  ref={gridRefs.current["grid1"]}
                  columnConfig={grids.grid1.columns}
                  rowData={grids.grid1.dataSource}
                  etcProps={{ rowSelection: "single", isEditable: false }}
                  events={grids.grid1.events}
                  // filter={{
                  //   refs: [gridRefs.current["grid2"]],
                  //   columns: ["cd_cust"],
                  // }}
                />
              </SnResizeBar>
              <SnResizeBar direction={"width"} id="panel2" orgSize="70%">
                <SnGrid
                  ref={gridRefs.current["grid2"]}
                  columnConfig={grids.grid2.columns}
                  rowData={grids.grid2.dataSource}
                  etcProps={{ rowSelection: "single" }}
                  events={grids.grid2.events}
                />
              </SnResizeBar>
            </div>,
            <div className="flex w-full h-full min-w-0 space-x-1.5">
              <SnResizeBar direction={"width"} id="panel3" orgSize="29.5%">
                <SnGrid
                  ref={gridRefs.current["grid3"]}
                  columnConfig={grids.grid3.columns}
                  rowData={grids.grid3.dataSource}
                  etcProps={{ rowSelection: "single", isEditable: false }}
                  events={grids.grid3.events}
                  // filter={{
                  //   refs: [gridRefs.current["grid4"]],
                  //   columns: ["cd_item"],
                  // }}
                />
              </SnResizeBar>
              <SnResizeBar direction={"width"} id="panel4" orgSize="70%">
                <SnGrid
                  ref={gridRefs.current["grid4"]}
                  columnConfig={grids.grid4.columns}
                  rowData={grids.grid4.dataSource}
                  etcProps={{ rowSelection: "single" }}
                  events={grids.grid4.events}
                />
              </SnResizeBar>
            </div>,
          ]}
        />
      </div>
    </div>
  );
});

export default SN_SAL_BAS_REG_002;
